import { Stack } from '@mantine/core'
import { BidParamSpec } from '@pubstack/common/src/bidderCatalog'
import { FunctionComponent } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Sizes } from '~/assets/style/tokens'
import { Title_P1 } from '~/components/_styled/titles'
import { RadioButtonGroup } from '~/components/RadioButtonGroup'
import { BidAdjustment } from '~/feature-components/BidAdjustment'
import { BidderSeatNameField, BidderSeatNameVariant } from './BidderSeatNameField'
import { CreateSeatFormModel } from './CreateSeatStepper'
import { DeclaredPlacementsSection } from './DeclaredPlacementsSection'
import { isPlacementMappedMethod, SeatSetupMethod, seatMappingTypeOptions } from './seatSetupOption'

type Props = {
  nameError: string
  onNameChange: () => void
  bidderDisplayName: string
  bidderCode: string
  bidParamSpecs: BidParamSpec[]
}

const nameVariantOf = (setupMethod: SeatSetupMethod | ''): BidderSeatNameVariant => (setupMethod === 'spark-demand' ? 'pbstck-bidder' : setupMethod === 'marketplace' ? 'marketplace' : 'pbs')

export const StepConfiguration: FunctionComponent<Props> = ({ nameError, onNameChange, bidderDisplayName, bidderCode, bidParamSpecs }) => {
  const { control } = useFormContext<CreateSeatFormModel>()
  const setupMethod = useWatch<CreateSeatFormModel, 'setupMethod'>({ name: 'setupMethod' })
  const isSparkForPub = setupMethod === 'spark-for-pubs'
  // Spark Demand is placement-mapped too, but the Spark team declares its placements elsewhere — so it
  // skips the mapping question without gaining the declared-placements section.
  const isPlacementMapped = !!setupMethod && isPlacementMappedMethod(setupMethod)

  return (
    <Stack gap={Sizes[16]}>
      <Stack gap={Sizes[16]}>
        <Title_P1>Identification</Title_P1>
        {/* Spark Demand and marketplace seats *must* end with `-spark` and `-pbstck`, which `BIDDER_SEAT_NAME_REGEX`
            — the `pbs` variant — forbids outright. The rules are exact opposites, so the field has to be told which. */}
        <BidderSeatNameField variant={nameVariantOf(setupMethod)} nameError={nameError} onChange={onNameChange} />
      </Stack>
      {isSparkForPub ? (
        <DeclaredPlacementsSection bidderDisplayName={bidderDisplayName} bidderCode={bidderCode} bidParamSpecs={bidParamSpecs} />
      ) : (
        <>
          {!isPlacementMapped && (
            <Stack gap={Sizes[10]}>
              <Title_P1>Mapping</Title_P1>
              <div className="font-roboto text-base leading-[1.625rem] text-jet">What each set of bid params applies to. The media type granularity can still be changed later.</div>
              {setupMethod && <RadioButtonGroup options={seatMappingTypeOptions(setupMethod)} name="mappingType" control={control} />}
            </Stack>
          )}
          <BidAdjustment trackingLabel="pbs-create-seat-stepper-config-bid-adjustment" />
        </>
      )}
    </Stack>
  )
}
